/**
 * ClientRequest REST Service
 */

import env from 'bench-environment';

export const createdStatus = 'CREATED';
export const completedStatus = 'COMPLETED';
export const generalRequestType = 'GENERAL';

// #region Types

export type ClientRequestStatus = 'CREATED' | 'COMPLETED';
export type ClientRequestType =
  | 'NONE'
  | 'ACCOUNT_CONNECT'
  | 'CATEGORIZATION'
  | 'DOCUMENT_UPLOAD'
  | 'GENERAL';

/**
 * Interface representing a new client request to be created.
 */
export interface NewClientRequest {
  name: string;
  requestType: ClientRequestType;
  metadata?: null | {
    url: string;
  };
}

/**
 * Interface representing an existing client request.
 */
export interface ClientRequest extends NewClientRequest {
  id: string;
  status: ClientRequestStatus;
  clientId: string;
  dateUpdated: string;
}

/**
 * Interface representing a list of client requests with summary information.
 */
export interface ClientRequestList {
  total: number;
  completed: number;
  requests: ClientRequest[];
}

/**
 * Interface representing an error response from the API.
 */
export interface ErrorResponse {
  response: {
    data: {
      messages: { message: string }[];
    };
  };
}

// #endregion Types

/**
 * Service class for managing client request API calls.
 * Handles all HTTP operations related to client requests including creation,
 * updates, and status changes.
 */
class ClientRequestService {
  #resource = 'clientrequest';
  #clientId: string;
  #baseUrl = `${env.location.apiRoot}/api/v1`;
  #fetch: typeof fetch;

  /**
   * Creates a new ClientRequestService instance.
   * @param clientId - The ID of the client for which requests will be managed
   * @param fetchImpl - Optional fetch implementation for testing/mocking
   */
  constructor(clientId: string, { fetchImpl }: { fetchImpl?: typeof fetch } = {}) {
    this.#clientId = clientId;
    this.#fetch = fetchImpl || fetch.bind(globalThis);
  }

  /**
   * Performs fetch with credentials included.
   * @private
   * @param url - The URL to fetch
   * @param options - Fetch options
   * @returns The fetch Response object
   */
  async #fetchWithCredentials(url: string, options: RequestInit = {}): Promise<Response> {
    return this.#fetch(url, {
      ...options,
      credentials: 'include', // Include cookies for authentication
    });
  }

  /**
   * Constructs the API URL for client request operations.
   * @param id - Optional request ID to append to the URL
   * @returns The complete API endpoint URL
   * @example
   * getUrl('123') // returns 'clientrequest.json/client/456/request/123'
   * getUrl() // returns 'clientrequest.json/client/456/request/'
   */
  getItemUrl(id = ''): string {
    return `${this.#baseUrl}/${this.#resource}.json/client/${this.#clientId}/request/${id}`;
  }

  /**
   * Constructs the API URL for listing all requests for the client.
   * @returns The complete API endpoint URL for listing requests
   * @example
   * getListUrl() // returns 'clientrequest.json/client/456/requests/'
   */
  getListUrl(): string {
    return `${this.#baseUrl}/${this.#resource}.json/client/${this.#clientId}/requests`;
  }

  /**
   * Adds the clientId to the request data -- the Angular model did this automatically.
   * @private
   * @param data - The request data object
   * @returns The request data object with clientId included
   */
  #addClientId(data: ClientRequest): ClientRequest {
    return {
      ...data,
      clientId: this.#clientId,
    };
  }

  /**
   * Handles the HTTP response and error formatting.
   * Throws errors in a format compatible with the existing error handling logic.
   * @private
   * @template T
   * @param response - The fetch Response object
   * @returns The parsed JSON response data
   * @throws {ErrorResponse} Error object with response.data structure for consistency with Angular model
   */
  async #handleResponse<T>(response: Response): Promise<T | null> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw {
        response: {
          data: errorData,
        },
      };
    }

    // 204 has no body
    return response.status === 204 ? null : response.json();
  }

  /**
   * Updates an existing client request with new data.
   * @param id - The ID of the request to update
   * @param data - The request data to update
   * @returns The updated request data from the server
   * @throws {ErrorResponse} Error object if the update fails
   */
  async update(id: string, data: Partial<ClientRequest>): Promise<ClientRequest | null> {
    const url = this.getItemUrl(id);
    const response = await this.#fetchWithCredentials(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const body = await this.#handleResponse<ClientRequest>(response);
    return body ? this.#addClientId(body) : null;
  }

  /**
   * Updates only the status of a client request.
   * Mirrors the $updateStatus method from the Angular ClientRequest model.
   * @param id - The ID of the request to update
   * @param status - The new status value (e.g., 'COMPLETED', 'CREATED')
   * @returns The updated request data from the server
   * @throws {ErrorResponse} Error object if the status update fails
   */
  async updateStatus(id: string, status: ClientRequestStatus): Promise<null> {
    const url = `${this.getItemUrl(id)}/status/${status}`;
    const response = await this.#fetchWithCredentials(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return this.#handleResponse(response);
  }

  /**
   * Creates a new client request.
   * @param data - The request data for the new request
   * @returns The newly created request data from the server
   * @throws {ErrorResponse} Error object if the creation fails
   */
  async create(data: NewClientRequest): Promise<ClientRequest> {
    const url = this.getItemUrl();
    const response = await this.#fetchWithCredentials(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    const body = (await this.#handleResponse<ClientRequest>(response)) as ClientRequest;
    return this.#addClientId(body);
  }

  /**
   * Retrieves a client request by ID.
   * @param id - The ID of the request to fetch
   * @returns The request data from the server
   * @throws {ErrorResponse} Error object if the fetch fails
   */
  async get(id: string): Promise<ClientRequest | null> {
    const url = this.getItemUrl(id);
    const response = await this.#fetchWithCredentials(url);
    const body = await this.#handleResponse<ClientRequest>(response);
    return body ? this.#addClientId(body) : null;
  }

  /**
   * Lists all client requests for the associated client.
   * @returns A list of client requests with summary information
   * @throws {ErrorResponse} Error object if the fetch fails
   */
  async list(): Promise<ClientRequestList> {
    const url = this.getListUrl();
    const response = await this.#fetchWithCredentials(url);
    const body = await this.#handleResponse<ClientRequestList>(response);
    return body
      ? {
          total: body.total,
          completed: body.completed,
          requests: body.requests.map(req => this.#addClientId(req)),
        }
      : { total: 0, completed: 0, requests: [] };
  }
}

export default ClientRequestService;
